// Unlike the Pórtico 2D matrix solver (which only returns nodal end forces and leaves the frontend
// to reconstruct V(x)/M(x) locally), the grid-solver backend already returns the full diagram per
// element (`element.diagram`: x, xi, w, beta, tau, V, M, T at each sampled point — see HANDOFF.md).
// So this module only has to extract/normalize those points and reconstruct the deflected shape;
// there is no local integration to do.

export function computeAllDiagrams(elements, resultElements) {
  const diagramsByElementId = {}
  const maxAbs = { V: 0, M: 0, T: 0, w: 0 }
  // Signed min/max per type (unlike maxAbs, which only tracks magnitude) — used to build a
  // diverging min→max color legend for the "heatmap" mesh coloring.
  const range = {
    V: { min: Infinity, max: -Infinity },
    M: { min: Infinity, max: -Infinity },
    T: { min: Infinity, max: -Infinity },
    w: { min: Infinity, max: -Infinity },
  }

  elements.forEach((el) => {
    const elementResult = resultElements.find((r) => r.id === el.id)
    const diagram = elementResult?.diagram
    if (!Array.isArray(diagram) || diagram.length === 0) return
    diagramsByElementId[el.id] = diagram
    ;['V', 'M', 'T', 'w'].forEach((type) => {
      const values = diagram.map((p) => Number(p[type]) || 0)
      const localMax = Math.max(...values.map((v) => Math.abs(v)))
      if (localMax > maxAbs[type]) maxAbs[type] = localMax
      const localMin = Math.min(...values)
      const localMaxSigned = Math.max(...values)
      if (localMin < range[type].min) range[type].min = localMin
      if (localMaxSigned > range[type].max) range[type].max = localMaxSigned
    })
  })

  return { diagramsByElementId, maxAbs, range }
}

function pointFraction(point, length) {
  if (point.xi !== undefined && point.xi !== null) return Number(point.xi)
  return length > 0 ? Number(point.x) / length : 0
}

// Reconstructs the deflected shape polyline in model coordinates directly from the backend's own
// w(x) samples — no moment-area reconstruction needed since the backend already solved for it.
// `scale` amplifies the (physically tiny) transverse displacement so the shape is visible. This is
// the plan-view version: since the canvas there only ever draws x/y, the (necessarily vertical,
// out-of-plane) displacement is faked as an in-plane offset along the element's normal `ey` — a
// legible "ribbon" wiggle rather than a true 3D bump. See computeDeformedElementPoints3D for the
// isometric view, which instead carries `w` on a real third axis.
export function computeDeformedElementPoints(nodeI, nodeJ, diagram, scale) {
  const dx = nodeJ.x - nodeI.x
  const dy = nodeJ.y - nodeI.y
  const length = Math.hypot(dx, dy) || 1
  const ex = { x: dx / length, y: dy / length }
  const ey = { x: -ex.y, y: ex.x }

  return diagram.map((point) => {
    const xi = Math.min(Math.max(pointFraction(point, length), 0), 1)
    const baseX = nodeI.x + ex.x * xi * length
    const baseY = nodeI.y + ex.y * xi * length
    const w = Number(point.w) || 0
    return { x: baseX + ey.x * scale * w, y: baseY + ey.y * scale * w }
  })
}

// Same reconstruction as computeDeformedElementPoints, but keeps `w` as a genuine third coordinate
// (`z`) instead of folding it into an in-plane offset — for the isometric view, which projects
// x/y/z with a real axonometric transform (see modelToIsoWorld in StructureCanvas.js) instead of
// drawing a flat x/y plan.
export function computeDeformedElementPoints3D(nodeI, nodeJ, diagram, scale) {
  const dx = nodeJ.x - nodeI.x
  const dy = nodeJ.y - nodeI.y
  const length = Math.hypot(dx, dy) || 1
  const ex = { x: dx / length, y: dy / length }

  return diagram.map((point) => {
    const xi = Math.min(Math.max(pointFraction(point, length), 0), 1)
    const w = Number(point.w) || 0
    return {
      x: nodeI.x + ex.x * xi * length,
      y: nodeI.y + ex.y * xi * length,
      z: scale * w,
    }
  })
}

// Shared by both the plan-view and isometric deformed-shape amplification: scales the largest
// transverse displacement in the result to read as ~8% of the structure's own size, times whatever
// factor the "Deformada" scale slider (viewScales.deformed) applies on top.
export function computeDeformedScale(nodes, maxDisp, viewScaleFactor) {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  nodes.forEach((n) => {
    minX = Math.min(minX, n.x)
    maxX = Math.max(maxX, n.x)
    minY = Math.min(minY, n.y)
    maxY = Math.max(maxY, n.y)
  })
  const diag = Math.hypot(maxX - minX, maxY - minY) || 1
  return (maxDisp > 0 ? (0.08 * diag) / maxDisp : 0) * viewScaleFactor
}

function elementDirection(nodeI, nodeJ) {
  const dx = nodeJ.x - nodeI.x
  const dy = nodeJ.y - nodeI.y
  const length = Math.hypot(dx, dy) || 1
  return { x: dx / length, y: dy / length }
}

export function isParallelDir(dirA, dirB) {
  return Math.abs(dirA.x * dirB.y - dirA.y * dirB.x) < 1e-6
}

function sourceId(el) {
  const id = el.source?.id
  return id === undefined ? null : id
}

// Partitions the whole discretized mesh into straight "lines of bars": chains of collinear
// elements connected end-to-end, which is what a single physical beam or slab strip gets
// subdivided into. Used both to isolate one line for a decluttered diagram view (clicking any
// element in it selects the whole chain) and, via estimateTributaryWidth below, to measure the
// spacing to neighboring parallel lines.
//
// Walks connected, collinear neighbors geometrically (a physical beam/strip is discretized into
// several bars in a straight run), additionally requiring `element.source.id` to match when the
// backend provides it on both elements. That guard stops the walk from jumping onto a *different*
// physical beam that just happens to be collinear at a shared node (which produced out-of-order,
// sawtooth diagrams) — geometry alone can't tell those apart, but they don't share a source id.
// Note this must stay a guard on top of the geometric walk, not a replacement for it: a slab's
// `source.id` identifies the whole physical slab, not a single row/column of its mesh, so grouping
// by source id alone would lump every element of a slab (in every direction) into one "alignment"
// the moment any of them share that id — which is exactly the bug where clicking one slab bar
// isolated nearly the entire mesh instead of just the straight line it's part of.
export function computeMeshAlignments(elements, nodesById) {
  const byNode = {}
  elements.forEach((el) => {
    ;(byNode[el.nodeI] ||= []).push(el)
    ;(byNode[el.nodeJ] ||= []).push(el)
  })

  const visited = new Set()
  const alignments = []

  elements.forEach((start) => {
    if (visited.has(start.id)) return
    const startNodeI = nodesById[start.nodeI]
    const startNodeJ = nodesById[start.nodeJ]
    if (!startNodeI || !startNodeJ) {
      visited.add(start.id)
      return
    }

    const dir = elementDirection(startNodeI, startNodeJ)
    const startSource = sourceId(start)
    const chain = [start]
    visited.add(start.id)

    // Greedily follows collinear neighbors outward from each end of the starting element until
    // no connected element continues in the same direction (and, when both sides have a source
    // id, shares the same one).
    function walk(fromNodeId, cameFromId) {
      let currentNodeId = fromNodeId
      let cameFrom = cameFromId
      for (;;) {
        const candidates = (byNode[currentNodeId] || []).filter((el) => el.id !== cameFrom && !visited.has(el.id))
        const next = candidates.find((el) => {
          const a = nodesById[el.nodeI]
          const b = nodesById[el.nodeJ]
          if (!a || !b || !isParallelDir(dir, elementDirection(a, b))) return false
          const candidateSource = sourceId(el)
          if (startSource !== null && candidateSource !== null && startSource !== candidateSource) return false
          return true
        })
        if (!next) break
        visited.add(next.id)
        chain.push(next)
        currentNodeId = next.nodeI === currentNodeId ? next.nodeJ : next.nodeI
        cameFrom = next.id
      }
    }
    walk(start.nodeI, start.id)
    walk(start.nodeJ, start.id)

    // Perpendicular offset from the origin: lets parallel chains be sorted/compared directly to
    // find neighboring lines and measure the spacing between them.
    const perp = { x: -dir.y, y: dir.x }
    const offset = startNodeI.x * perp.x + startNodeI.y * perp.y

    alignments.push({ elementIds: chain.map((el) => el.id), dir, offset })
  })

  return alignments
}

// Estimates the tributary width of the alignment containing `elementId` as the distance to its
// nearest parallel neighboring alignment on each side (averaged when both exist; a single-side
// distance at a mesh boundary, matching the half-width edge strip an FEM mesh implies there).
// Falls back to `fallbackWidth` (e.g. mesh.maxSpacing) when no parallel neighbor exists at all —
// an isolated beam that isn't part of a grid of parallel lines.
export function estimateTributaryWidth(elementId, alignments, fallbackWidth) {
  const own = alignments.find((a) => a.elementIds.includes(elementId))
  if (!own) return fallbackWidth

  const sameDirection = alignments.filter((a) => isParallelDir(a.dir, own.dir))
  const offsets = sameDirection.map((a) => a.offset).sort((a, b) => a - b)
  const ownIndex = offsets.findIndex((o) => Math.abs(o - own.offset) < 1e-9)

  const below = ownIndex > 0 ? offsets[ownIndex - 1] : null
  const above = ownIndex >= 0 && ownIndex < offsets.length - 1 ? offsets[ownIndex + 1] : null

  if (below !== null && above !== null) return (above - below) / 2
  if (below !== null) return own.offset - below
  if (above !== null) return above - own.offset
  return fallbackWidth
}

// Stitches one or more collinear alignments' per-element diagrams into one continuous V/M/T/w
// profile (position along the shared axis), for plotting as a standalone chart away from the plan
// view instead of overlaying a diagram directly on the geometry. `alignment.elementIds` may come
// from a single alignment or from several merged ones (e.g. two slabs on the same row, split by a
// crossing beam's differing source id — see the shift-click merge in StructureCanvas.js); either
// way every element's samples are placed by their real projected position along `alignment.dir`,
// so a gap between non-touching chains shows up as a gap in the chart rather than being papered
// over. An element whose own nodeI->nodeJ sense runs opposite to that direction has its samples
// reversed and its V/T sign flipped (the standard mechanics convention: shear and torsion sign
// depends on which way you traverse the axis, bending moment does not).
export function buildAlignmentProfile(alignment, elements, nodesById, diagramsByElementId) {
  const elementsById = {}
  elements.forEach((el) => {
    elementsById[el.id] = el
  })

  const ordered = alignment.elementIds
    .map((id) => elementsById[id])
    .filter(Boolean)
    .map((el) => {
      const nodeI = nodesById[el.nodeI]
      const nodeJ = nodesById[el.nodeJ]
      const projI = nodeI.x * alignment.dir.x + nodeI.y * alignment.dir.y
      const projJ = nodeJ.x * alignment.dir.x + nodeJ.y * alignment.dir.y
      return { el, nodeI, nodeJ, projI, projJ, projection: (projI + projJ) / 2 }
    })
    .sort((a, b) => a.projection - b.projection)

  if (ordered.length === 0) return []
  const origin = Math.min(...ordered.map((o) => Math.min(o.projI, o.projJ)))

  const profile = []
  ordered.forEach(({ el, nodeI, nodeJ, projI, projJ }, index) => {
    const diagram = diagramsByElementId[el.id]
    if (!nodeI || !nodeJ || !diagram) return
    const length = Math.hypot(nodeJ.x - nodeI.x, nodeJ.y - nodeI.y) || 0
    const forward = projJ >= projI
    const basePos = Math.min(projI, projJ) - origin

    // Elements sharing a boundary node with the previous one in the walk would otherwise
    // duplicate its last sample as their own first — skip it in that case. Elements separated by
    // a real gap (no shared node, e.g. the crossing beam between two merged slabs) don't share a
    // sample there, so both endpoints are kept and the gap shows up in the chart.
    const prev = ordered[index - 1]
    const touchesPrev =
      !!prev &&
      (prev.el.nodeI === el.nodeI ||
        prev.el.nodeI === el.nodeJ ||
        prev.el.nodeJ === el.nodeI ||
        prev.el.nodeJ === el.nodeJ)

    diagram.forEach((p, idx) => {
      if (touchesPrev && idx === 0) return
      const localX = forward ? Number(p.x) : length - Number(p.x)
      profile.push({
        position: basePos + localX,
        V: (Number(p.V) || 0) * (forward ? 1 : -1),
        M: Number(p.M) || 0,
        T: (Number(p.T) || 0) * (forward ? 1 : -1),
        w: Number(p.w) || 0,
      })
    })
  })

  return profile
}
